
import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { CATEGORIES, MIN_HIRE_OPTIONS } from '../lib/constants'
import { describeSignUpError, describeSignInError, looksLikeEmail } from '../lib/authErrors'
import { openExternal } from '../lib/openExternal'
import { track } from '../lib/analytics'
import { useAuth } from '../context/AuthContext'
import Header from '../components/Header'
import { prepareImageForUpload } from '../lib/imageUpload'
import ContactWarning from '../components/ContactWarning'
import { checkAndLogContact } from '../lib/contactFlags'

const DRAFT_KEY = 'hireit_draft_listing_v1'
const MIN_DESCRIPTION_LENGTH = 10
const MAX_DESCRIPTION_LENGTH = 1000


const getSuggestedRates = (itemValue) => {
const val = parseFloat(itemValue)
if (!val || val <= 0) return null

let hourlyPct
if (val < 100) hourlyPct = 0.020
else if (val < 500) hourlyPct = 0.010
else if (val < 2000) hourlyPct = 0.005
else if (val < 10000) hourlyPct = 0.003
else hourlyPct = 0.0015

const rawHourly = Math.round(val * hourlyPct * 2) / 2
const hourly = Math.max(10, rawHourly)
const rawDaily = Math.round(rawHourly * 6)
const daily = Math.max(rawDaily, Math.round(hourly * 1.5))
const weekly = Math.round(daily * 4)
const deposit = Math.round(val * 0.15)

return { hourly, daily, weekly, deposit }
}

const getSuggestedDepositFromHourly = (pricePerHour) => {
const price = parseFloat(pricePerHour)
if (!price || price <= 0) return null
if (price <= 10) return Math.round(price * 5)
if (price <= 30) return Math.round(price * 8)
if (price <= 100) return Math.round(price * 6)
return Math.round(price * 4)
}

const INITIAL_FORM = {
title: '', description: '', category_id: '', price_per_hour: '',
price_per_day: '', price_per_week: '',
deposit: '', item_value: '', postcode: '', suburb: '', state: '', country: 'Australia',
lat: '', lng: '', full_address: '',
min_hours: 1, max_hours: 120, rules: '', owner_insured: null,
insureAgree: false, ownershipAgree: false, termsAgree: false, requires_id: false, includes_operator: false,
}

export default function ListItem() {
const { user, profile, signIn, signUp } = useAuth()
const navigate = useNavigate()
const [searchParams] = useSearchParams()
const [step, setStep] = useState(1)
const [submitting, setSubmitting] = useState(false)
const [error, setError] = useState('')
const [success, setSuccess] = useState(false)
const [images, setImages] = useState([])
const [uploading, setUploading] = useState(false)
const [connecting, setConnecting] = useState(false)
const [depositTooltip, setDepositTooltip] = useState(false)
const autocompleteRef = useRef(null)
const autocompleteContainerRef = useRef(null)
// Holds File objects for photos added while logged out — not persisted across refreshes
const pendingFilesRef = useRef([])
const [lostPhotoCount, setLostPhotoCount] = useState(0)
const [pendingPublish, setPendingPublish] = useState(false)
const [draftLoaded, setDraftLoaded] = useState(false)
// Step 4 state
const [s4Mode, setS4Mode] = useState('signup') // 'signup' | 'signin' | 'pending'
const [s4Name, setS4Name] = useState('')
const [s4Email, setS4Email] = useState('')
const [s4Password, setS4Password] = useState('')
const [s4Terms, setS4Terms] = useState(false)
const [s4Loading, setS4Loading] = useState(false)
const [s4Error, setS4Error] = useState('')
const [s4Errors, setS4Errors] = useState({})
const [form, setForm] = useState(INITIAL_FORM)

const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

// ── Draft: restore on mount ────────────────────────────────────────────────
useEffect(() => {
const raw = localStorage.getItem(DRAFT_KEY)
if (raw) {
  try {
    const { form: f, images: imgs, pendingPhotoCount: pc, pendingPublish: pp } = JSON.parse(raw)
    if (f) setForm(prev => ({ ...prev, ...f }))
    if (imgs?.length) setImages(imgs)
    if (pc > 0) setLostPhotoCount(pc)
    if (pp) setPendingPublish(true)
  } catch {}
}
setDraftLoaded(true)
}, [])

// ── Draft: save on every change (skip until initial load to avoid overwriting with defaults) ──
useEffect(() => {
if (!draftLoaded) return
const uploadedImages = images.filter(u => !u.startsWith('blob:'))
const pendingCount = images.filter(u => u.startsWith('blob:')).length + pendingFilesRef.current.length
localStorage.setItem(DRAFT_KEY, JSON.stringify({
  form,
  images: uploadedImages,
  pendingPhotoCount: pendingCount,
  pendingPublish,
}))
}, [form, images, pendingPublish, draftLoaded])

// ── Auto-publish once user signs in with a pending draft ───────────────────
useEffect(() => {
if (!user || !pendingPublish || !draftLoaded) return
if (!form.title.trim() || !form.category_id) {
  // Draft is empty/garbage — discard silently
  localStorage.removeItem(DRAFT_KEY)
  setPendingPublish(false)
  return
}
// If photos were lost on refresh, don't silently publish with missing photos.
// Take them back to step 1 so they can re-add before publishing manually.
if (lostPhotoCount > 0) {
  setPendingPublish(false)
  setStep(1)
  return
}
doPublish(pendingFilesRef.current)
}, [user, pendingPublish, draftLoaded]) // form/images/lostPhotoCount read via closure — intentional

// ── Stripe connect ─────────────────────────────────────────────────────────
const connectStripe = async () => {
setConnecting(true)
try {
  const { data: emailData } = await supabase.rpc('get_user_email', { user_id: user.id })
  const { data: { session } } = await supabase.auth.getSession()
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://hireit-production-ac0f.up.railway.app'}/stripe/connect/onboard`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
    body: JSON.stringify({ userId: user.id, email: emailData })
  })
  const data = await res.json()
  if (data.url) {
    await supabase.from('profiles').update({ stripe_account_id: data.accountId }).eq('id', user.id)
    await openExternal(data.url)
  }
} catch (e) {
  console.error('Stripe connect error:', e)
}
setConnecting(false)
}

// ── Google Places autocomplete (step 2 only) ───────────────────────────────
useEffect(() => {
if (step !== 2) return
let attempts = 0
const interval = setInterval(() => {
  attempts += 1
  if (window.google?.maps?.places?.PlaceAutocompleteElement && autocompleteContainerRef.current && !autocompleteRef.current) {
    const el = new window.google.maps.places.PlaceAutocompleteElement({ includedRegionCodes: ['au'] })
    el.className = 'form-control'
    el.placeholder = 'Start typing your city, suburb or address...'
    autocompleteContainerRef.current.appendChild(el)
    autocompleteRef.current = el
    el.addEventListener('gmp-select', async ({ placePrediction }) => {
      const place = placePrediction.toPlace()
      await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'] })
      const get = (type) => place.addressComponents?.find(c => c.types.includes(type))?.longText || ''
      setForm(f => ({
        ...f,
        full_address: place.formattedAddress,
        suburb: get('locality') || get('sublocality') || get('neighborhood') || get('administrative_area_level_3') || get('administrative_area_level_2') || placePrediction.mainText?.text || '',
        state: get('administrative_area_level_1'),
        postcode: get('postal_code'),
        country: get('country'),
        lat: place.location.lat(),
        lng: place.location.lng(),
      }))
    })
    track('places_ready')
    clearInterval(interval)
  } else if (attempts >= 20) {
    track('places_unavailable')
    clearInterval(interval)
  }
}, 500)
return () => {
  clearInterval(interval)
  if (autocompleteRef.current && autocompleteContainerRef.current?.contains(autocompleteRef.current)) {
    autocompleteContainerRef.current.removeChild(autocompleteRef.current)
  }
  autocompleteRef.current = null
}
}, [step])

// ── Photo upload ───────────────────────────────────────────────────────────
const handleImageUpload = async (e) => {
const files = Array.from(e.target.files)
if (files.length === 0) return
if (images.length + files.length > 5) { setError('Maximum 5 photos allowed'); return }
setUploading(true); setError('')
setLostPhotoCount(0)

if (!user) {
  // Logged-out: store File objects locally, show blob: preview URLs
  const previews = []
  for (const file of files) {
    const previewUrl = URL.createObjectURL(file)
    pendingFilesRef.current = [...pendingFilesRef.current, { file, previewUrl }]
    previews.push(previewUrl)
  }
  setImages(prev => [...prev, ...previews])
  setUploading(false)
  return
}

// Logged-in: upload immediately
const uploadedUrls = []
for (const file of files) {
  let fileToUpload
  try {
    fileToUpload = await prepareImageForUpload(file)
  } catch (prepErr) {
    console.error('Image prep failed:', prepErr)
    setError(`Could not process ${file.name}. Try a JPEG or PNG instead.`)
    setUploading(false)
    return
  }
  try {
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.jpg`
    const { error: uploadError } = await supabase.storage
      .from('listing-images').upload(fileName, fileToUpload, { cacheControl: '31536000', upsert: false, contentType: 'image/jpeg' })
    if (uploadError) { setError('Failed to upload: ' + uploadError.message); setUploading(false); return }
    const { data: { publicUrl } } = supabase.storage.from('listing-images').getPublicUrl(fileName)
    uploadedUrls.push(publicUrl)
  } catch (e) {
    console.error('Upload error:', e)
    setError('Failed to upload photo. Please try again.')
    setUploading(false)
    return
  }
}
setImages(prev => [...prev, ...uploadedUrls])
setUploading(false)
}

const removeImage = (index) => {
const url = images[index]
if (url?.startsWith('blob:')) {
  pendingFilesRef.current = pendingFilesRef.current.filter(p => p.previewUrl !== url)
  URL.revokeObjectURL(url)
}
setImages(prev => prev.filter((_, i) => i !== index))
}

// ── Validation ─────────────────────────────────────────────────────────────
const validateStep1 = () => {
if (!form.title.trim()) return 'Please enter a title'
if (!form.category_id) return 'Please choose a category'
if (!form.description.trim()) return 'Please add a description'
if (form.description.trim().length < MIN_DESCRIPTION_LENGTH) return `Description must be at least ${MIN_DESCRIPTION_LENGTH} characters`
return null
}

const validateStep2 = () => {
const hourlyPrice = form.price_per_hour ? parseFloat(form.price_per_hour) : null
const dailyPrice = form.price_per_day ? parseFloat(form.price_per_day) : null
const weeklyPrice = form.price_per_week ? parseFloat(form.price_per_week) : null
if (!hourlyPrice && !dailyPrice && !weeklyPrice) return 'Please set at least one rate (hourly, daily, or weekly).'
if (form.price_per_hour && (!hourlyPrice || hourlyPrice < 10)) return 'Hourly rate must be at least $10.'
if (dailyPrice && hourlyPrice && dailyPrice < hourlyPrice) return `Daily rate can't be less than your hourly rate ($${hourlyPrice.toFixed(2)}).`
if (weeklyPrice) {
  const floor = dailyPrice || hourlyPrice
  if (floor && weeklyPrice < floor) {
    const floorLabel = dailyPrice ? 'daily rate' : 'hourly rate'
    return `Weekly rate can't be less than your ${floorLabel} ($${floor.toFixed(2)}).`
  }
}
if (!form.full_address.trim()) return 'Please enter your location'
return null
}

const handleNext = () => {
setError('')
const err = step === 1 ? validateStep1() : validateStep2()
if (err) { setError(err); return }
track('list_step_advance', { to_step: step + 1 })
// Admin funnel: first time a signed-in user gets past step 1
if (step === 1 && user && profile && !profile.listing_started_at) {
supabase.from('profiles').update({ listing_started_at: new Date().toISOString() }).eq('id', user.id).then(() => {})
}
setStep(s => s + 1)
}

// Called when logged-out user taps "Continue →" on step 3
const handleStep3Continue = () => {
setError('')
if (form.owner_insured === null) { setError('Please choose an insurance status.'); return }
if (!form.insureAgree || !form.ownershipAgree || !form.termsAgree) { setError('Please accept all agreements.'); return }
track('sign_up_view', { source: 'list_item' })
setStep(4)
}

// ── Core publish (used both by logged-in direct submit and auto-publish after sign-in) ──
const doPublish = async (filesToUpload = []) => {
track('list_submit_attempt')
if (form.owner_insured === null) { setError('Please choose an insurance status.'); return }
if (!form.insureAgree || !form.ownershipAgree || !form.termsAgree) { setError('Please accept all agreements.'); return }
if (!user) { setError('Please sign in to publish.'); return }
if (!form.title.trim()) { setError('Please enter a title.'); return }
if (!form.category_id) { setError('Please choose a category.'); return }
// Contact details in the title/description: warning only for now, logged for Admin → Flags
checkAndLogContact('listing', `${form.title}\n${form.description}`)
const hourlyPrice = form.price_per_hour ? parseFloat(form.price_per_hour) : null
const dailyPrice = form.price_per_day ? parseFloat(form.price_per_day) : null
const weeklyPrice = form.price_per_week ? parseFloat(form.price_per_week) : null
if (!hourlyPrice && !dailyPrice && !weeklyPrice) { setError('Please set at least one rate (hourly, daily, or weekly).'); return }
if (form.price_per_hour && (!hourlyPrice || hourlyPrice < 10)) { setError('Hourly rate must be at least $10.'); return }
if (dailyPrice && hourlyPrice && dailyPrice < hourlyPrice) { setError(`Daily rate can't be less than your hourly rate ($${hourlyPrice.toFixed(2)}).`); return }
if (weeklyPrice) {
  const floor = dailyPrice || hourlyPrice
  if (floor && weeklyPrice < floor) {
    const floorLabel = dailyPrice ? 'daily rate' : 'hourly rate'
    setError(`Weekly rate can't be less than your ${floorLabel} ($${floor.toFixed(2)}).`); return
  }
}
if (!form.lat || !form.lng) { setError('Please select your address from the dropdown suggestions so your listing appears in search.'); return }

// Upload any pending files collected while logged out
let allImages = images.filter(u => !u.startsWith('blob:'))
if (filesToUpload.length > 0) {
  setUploading(true)
  for (const { file } of filesToUpload) {
    let fileToUpload
    try { fileToUpload = await prepareImageForUpload(file) } catch { continue }
    try {
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.jpg`
      const { error: uploadError } = await supabase.storage
        .from('listing-images').upload(fileName, fileToUpload, { cacheControl: '31536000', upsert: false, contentType: 'image/jpeg' })
      if (!uploadError) {
        const { data: { publicUrl } } = supabase.storage.from('listing-images').getPublicUrl(fileName)
        allImages.push(publicUrl)
      }
    } catch {}
  }
  pendingFilesRef.current = []
  setUploading(false)
}

setSubmitting(true); setError('')
const { error: err } = await supabase.from('listings').insert({
  owner_id: user.id,
  title: form.title,
  description: form.description,
  category_id: parseInt(form.category_id),
  price_per_hour: form.price_per_hour ? parseFloat(form.price_per_hour) : null,
  price_per_day: form.price_per_day ? parseFloat(form.price_per_day) : null,
  price_per_week: form.price_per_week ? parseFloat(form.price_per_week) : null,
  deposit: parseFloat(form.deposit) || 0,
  postcode: form.postcode,
  suburb: form.suburb,
  state: form.state,
  country: form.country,
  lat: form.lat ? parseFloat(form.lat) : null,
  lng: form.lng ? parseFloat(form.lng) : null,
  full_address: form.full_address,
  min_hours: parseInt(form.min_hours),
  max_hours: parseInt(form.max_hours),
  rules: form.rules,
  images: allImages,
  owner_insured: form.owner_insured,
  ownership_confirmed: form.ownershipAgree,
  requires_id: form.requires_id,
  includes_operator: form.includes_operator,
  available: true,
  // Live straight away — email verification is only needed before accepting a booking
  status: 'active',
})
setSubmitting(false)
if (err) {
  setError(err.message)
  track('list_submit_error', { message: String(err?.message || err).slice(0, 100) })
} else {
  // Copy listing suburb into profile if profile has none
  if (!profile?.suburb && form.suburb) {
    try { await supabase.from('profiles').update({ suburb: form.suburb }).eq('id', user.id) } catch {}
  }
  localStorage.removeItem(DRAFT_KEY)
  setPendingPublish(false)
  setSuccess(true)
  track('list_submit_success')
  // GA4 key event (no personal details sent)
  track('listing_created', {
    category: CATEGORIES.find(c => c.id === parseInt(form.category_id))?.slug || 'unknown',
    photo_count: allImages.length,
    includes_operator: !!form.includes_operator,
  })
  if (!profile?.email_verified) {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://hireit-production-ac0f.up.railway.app'}/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
      })
    } catch (e) {
      console.error('send-verification failed', e)
    }
  }
}
}

// ── Step 4: sign-up handler ────────────────────────────────────────────────
const handleS4SignUp = async () => {
track('sign_up_submit', { source: 'list_item' })
setS4Error('')
const errs = {}
if (!s4Name.trim()) errs.name = 'Please enter your full name.'
if (!s4Email.trim()) errs.email = 'Please enter your email address.'
else if (!looksLikeEmail(s4Email)) errs.email = "That email address doesn't look right — please check it."
if (s4Password.length < 6) errs.password = 'Password must be at least 6 characters.'
if (!s4Terms) errs.terms = 'Please tick the box to accept the Terms and Conditions.'
setS4Errors(errs)
if (Object.keys(errs).length) {
track('sign_up_error', { reason: Object.keys(errs).map(k => `missing_${k}`).join(',').slice(0, 100), source: 'list_item' })
// Bring the first error into view (it can be off-screen on a phone)
requestAnimationFrame(() => document.querySelector('.form-error, .alert-error')?.scrollIntoView({ block: 'center', behavior: 'smooth' }))
return
}
setS4Loading(true)
const { data, error } = await signUp(s4Email.trim(), s4Password, s4Name.trim(), '', null, window.location.origin + '/list-item?confirmed=1', 'list_item')
setS4Loading(false)
const alreadyRegistered = !error && data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0
if (error || alreadyRegistered) {
const info = describeSignUpError(alreadyRegistered ? { code: 'user_already_exists' } : error)
setS4Errors(info.field ? { [info.field]: info.message, alreadyRegistered: info.reason === 'already_registered' } : { general: info.message })
track('sign_up_error', { reason: info.reason, source: 'list_item' })
// Bring the first error into view (it can be off-screen on a phone)
requestAnimationFrame(() => document.querySelector('.form-error, .alert-error')?.scrollIntoView({ block: 'center', behavior: 'smooth' }))
return
}
track('sign_up', { method: 'email', form: 'list_item', source: 'list_item' })
setPendingPublish(true)
if (data?.session) {
// Auto-confirmed: already signed in, so the publish effect runs straight away.
// They're listing, so record them as an owner (they skip the onboarding question).
supabase.from('profiles').update({ user_intent: 'earn' }).eq('id', data.session.user.id).is('user_intent', null).then(() => {}, () => {})
return
}
// Only reachable if Supabase is ever switched to require email confirmation
setS4Mode('pending')
}

// ── Step 4: sign-in handler ────────────────────────────────────────────────
const handleS4SignIn = async () => {
setS4Error('')
if (!s4Email.trim()) { setS4Error('Please enter your email address'); return }
if (!s4Password) { setS4Error('Please enter your password'); return }
setS4Loading(true)
const { error } = await signIn(s4Email.trim(), s4Password)
setS4Loading(false)
if (error) { setS4Error(describeSignInError(error)); return }
// onAuthStateChange fires → user is set → auto-publish useEffect triggers
setPendingPublish(true)
}

// ── Computed values ────────────────────────────────────────────────────────
const suggested = getSuggestedRates(form.item_value)
const fallbackDeposit = getSuggestedDepositFromHourly(form.price_per_hour)
const descLen = form.description.trim().length
const descTooShort = descLen > 0 && descLen < MIN_DESCRIPTION_LENGTH
const descOk = descLen >= MIN_DESCRIPTION_LENGTH
const missingMultiDayRates = !!form.price_per_hour && !form.price_per_day && !form.price_per_week
const isStripeReady = !!profile?.stripe_charges_enabled
const hasAnyPrice = form.price_per_hour || form.price_per_day || form.price_per_week
const hireOptions = form.price_per_hour ? MIN_HIRE_OPTIONS : MIN_HIRE_OPTIONS.filter(o => o.value >= 24)
const stepLabels = user
? ['Item details', 'Pricing & location', 'Confirm']
: ['Item details', 'Pricing & location', 'Confirm', 'Create account']
// True when user lands via the confirmation email link but the draft is on a different browser/device
const confirmedElsewhere = searchParams.get('confirmed') === '1' && !!user && draftLoaded && !pendingPublish

useEffect(() => {
if (form.price_per_hour) return
setForm(f => {
  const next = { ...f }
  if (parseInt(f.min_hours) < 24) next.min_hours = 24
  if (parseInt(f.max_hours) < 24) next.max_hours = 168
  return next
})
}, [form.price_per_hour])

const applyAllSuggested = () => {
if (!suggested) return
setForm(f => ({
  ...f,
  price_per_hour: String(suggested.hourly),
  price_per_day: String(suggested.daily),
  price_per_week: String(suggested.weekly),
  deposit: String(suggested.deposit),
}))
}

const resetForm = () => {
setSuccess(false)
setStep(1)
setImages([])
pendingFilesRef.current = []
setLostPhotoCount(0)
setPendingPublish(false)
setS4Mode('signup')
setS4Name(''); setS4Email(''); setS4Password(''); setS4Terms(false); setS4Error('')
localStorage.removeItem(DRAFT_KEY)
setForm(INITIAL_FORM)
}

// ── Success screen ─────────────────────────────────────────────────────────
if (success) return (
<div><Header />
<div className="container" style={{ maxWidth: 520, padding: '3rem 1rem', textAlign: 'center' }}>
  <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
  <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>Item listed!</h2>
  <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>Your item is now live and visible to hirers near you.</p>
  {!profile?.email_verified && (
    <p style={{ fontSize: 13, color: 'var(--text-muted)', background: 'var(--amber-light)', borderRadius: 10, padding: '10px 14px', marginBottom: 24, textAlign: 'left' }}>
      📩 We've emailed you a link to confirm your email address. You'll need to do this before you can accept bookings.
    </p>
  )}

  {!isStripeReady && (
    <div style={{ background: 'var(--primary-pale)', border: '2px solid var(--primary)', borderRadius: 16, padding: '1.5rem', marginBottom: 24, textAlign: 'left' }}>
      <div style={{ fontSize: 24, marginBottom: 8 }}>💳</div>
      <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--navy)' }}>Connect your bank account</div>
      <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>To receive payments when your item is hired, you need to connect your bank account via Stripe. It only takes a few minutes.</p>
      <button className="btn btn-gradient btn-full" onClick={() => { track('stripe_connect_start'); connectStripe() }} disabled={connecting}>
        {connecting ? 'Connecting...' : '💳 Connect bank account'}
      </button>
      <button className="btn btn-ghost btn-full" style={{ marginTop: 8 }} onClick={() => navigate('/')}>
        Do this later
      </button>
    </div>
  )}

  {isStripeReady && (
    <div style={{ background: '#E8F8EE', border: '1px solid #16A34A', borderRadius: 12, padding: '14px 18px', marginBottom: 24, fontSize: 14, color: '#15803D', fontWeight: 600 }}>
      ✅ Bank account already connected — you're ready to receive payments
    </div>
  )}

  <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
    <button className="btn btn-gradient" onClick={() => navigate('/')}>Browse listings</button>
    <button className="btn btn-ghost" onClick={() => navigate('/my-listings')}>View my listings</button>
    <button className="btn btn-ghost" onClick={resetForm}>List another</button>
  </div>
</div>
</div>
)

// ── Main form ──────────────────────────────────────────────────────────────
return (
<div>
<Header />
<div className="container" style={{ maxWidth: 600, padding: '1.5rem 1rem' }}>
  <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: '1rem' }}>← Back</button>
  <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4, color: 'var(--navy)' }}>List your item</h1>
  <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>It can be almost anything — list it free and set your own price.</p>

  {/* Different-browser confirmation banner */}
  {confirmedElsewhere && (
    <div style={{ background: '#E8F8EE', border: '1px solid #16A34A', borderRadius: 10, padding: '14px 16px', marginBottom: '1.5rem', fontSize: 14, color: '#15803D', lineHeight: 1.5 }}>
      ✅ <strong>You're confirmed!</strong> If you started a listing on another device or browser, go back to that page and sign in to publish it — or start a new listing here.
    </div>
  )}

  {/* Step progress bar */}
  <div style={{ display: 'flex', gap: 8, marginBottom: '1.5rem' }}>
    {stepLabels.map((label, i) => (
      <div key={label} style={{ flex: 1, textAlign: 'center' }}>
        <div style={{ height: 4, borderRadius: 99, background: i < step ? 'var(--primary)' : 'var(--gray-200)', marginBottom: 6 }} />
        <div style={{ fontSize: 11, color: i < step ? 'var(--primary)' : 'var(--text-faint)', fontWeight: i + 1 === step ? 600 : 500 }}>{label}</div>
      </div>
    ))}
  </div>

  <div className="card card-body">

    {/* Lost-photo notice — only shown when photos were actually lost on refresh */}
    {lostPhotoCount > 0 && step === 1 && (
      <div style={{ background: '#FFF8E7', border: '1px solid #F5C842', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#8a6d00', lineHeight: 1.5 }}>
        ⚠️ {lostPhotoCount} photo{lostPhotoCount > 1 ? 's were' : ' was'} in your draft but couldn't be restored — photos aren't saved across page refreshes. Please re-add {lostPhotoCount > 1 ? 'them' : 'it'} below.
      </div>
    )}

    {/* ── Step 1: Item details ── */}
    {step === 1 && (
      <>
      <div className="form-group">
        <label className="form-label">Item title <span style={{ color: '#DC2626' }}>*</span> <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(required)</span></label>
        <input className="form-control" placeholder="e.g. Makita 18V Drill Set" value={form.title} onChange={e => set('title', e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label">Category <span style={{ color: '#DC2626' }}>*</span> <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(required)</span></label>
        <select className="form-control" value={form.category_id} onChange={e => set('category_id', e.target.value)}>
          <option value="">Select a category...</option>
          {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Description <span style={{ color: '#DC2626' }}>*</span> <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(required — minimum {MIN_DESCRIPTION_LENGTH} characters)</span></label>
        <textarea
          className="form-control"
          placeholder="Describe your item — condition, age, what's included, pickup notes, any requirements..."
          value={form.description}
          onChange={e => {
            if (e.target.value.length <= MAX_DESCRIPTION_LENGTH) set('description', e.target.value)
          }}
          style={{ minHeight: 100, borderColor: descTooShort ? 'var(--amber)' : descOk ? '#16A34A' : undefined }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 6 }}>
          <span style={{ color: descTooShort ? 'var(--amber)' : descOk ? '#16A34A' : 'var(--text-muted)' }}>
            {descLen === 0 && "Tip: describe what it is, its condition, what's included"}
            {descTooShort && `⚠️ ${MIN_DESCRIPTION_LENGTH - descLen} more characters needed`}
            {descOk && '✓ Looks good'}
          </span>
          <span style={{ color: 'var(--text-faint)' }}>{descLen} / {MAX_DESCRIPTION_LENGTH}</span>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Photos (up to 5)</label>
        <div style={{ border: '2px dashed var(--border-strong)', borderRadius: 12, padding: '1.5rem', textAlign: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
          <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 12 }}>Upload photos of your item</p>
          <label style={{ cursor: 'pointer' }}>
            <span className="btn btn-ghost btn-sm">{uploading ? 'Uploading...' : 'Choose photos'}</span>
            <input type="file" accept="image/*" multiple onChange={handleImageUpload} style={{ display: 'none' }} disabled={uploading} />
          </label>
        </div>
        {images.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }}>
            {images.map((url, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <img src={url} alt={`Upload ${i+1}`} style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 8 }} />
                <button onClick={() => removeImage(i)} style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 12 }}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="form-group">
        <label className="form-label">Hire rules (optional)</label>
        <textarea className="form-control" style={{ minHeight: 70 }} placeholder="e.g. Must provide ID, no interstate travel, return clean..." value={form.rules} onChange={e => set('rules', e.target.value)} />
        <div style={{ background: '#FFF8E7', border: '1px solid #F5C842', borderRadius: 8, padding: '10px 14px', marginTop: 6, fontSize: 12, color: '#8a6d00', lineHeight: 1.6 }}>
          💡 <strong>Tip:</strong> Damage and late-return cover is already handled by your security deposit (set on the next step) — you don't need to include payment or compensation terms here. Use this space for practical rules instead (e.g. no smoking, return with a full tank of fuel).
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">ID requirement (optional)</label>
        <label style={{ display: 'flex', gap: 10, cursor: 'pointer', fontSize: 13, padding: '10px 14px', borderRadius: 8, border: `1px solid ${form.requires_id ? '#F5C842' : 'var(--border)'}`, background: form.requires_id ? '#FFF8E7' : 'white' }}>
          <input type="checkbox" checked={form.requires_id || false} onChange={e => set('requires_id', e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>🪪 <strong>Require hirer to show driver's licence at pickup</strong> — useful for vehicles, trailers, or high-value items. You visually verify the licence in person (HireIt does not store any licence data).</span>
        </label>
      </div>
      <div className="form-group">
        <label className="form-label">Operator included (optional)</label>
        <label style={{ display: 'flex', gap: 10, cursor: 'pointer', fontSize: 13, padding: '10px 14px', borderRadius: 8, border: `1px solid ${form.includes_operator ? 'var(--primary)' : 'var(--border)'}`, background: form.includes_operator ? 'var(--primary-light)' : 'white' }}>
          <input type="checkbox" checked={form.includes_operator || false} onChange={e => set('includes_operator', e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>🛠️ <strong>This listing includes me as the operator</strong> — turn this on if you'll be operating the equipment yourself as part of the hire (e.g. rotary hoe with driver, bobcat with operator, trailer + labour).</span>
        </label>
      </div>
      </>
    )}

    {/* ── Step 2: Pricing & location ── */}
    {step === 2 && (
      <>
      <div className="form-group">
        <label className="form-label">Item value (optional)</label>
        <input className="form-control" type="number" placeholder="e.g. 500" min="0" value={form.item_value} onChange={e => set('item_value', e.target.value)} />
        <div className="form-hint">Estimated replacement value of your item in dollars</div>
        {suggested && (
          <div style={{ background: 'linear-gradient(135deg, rgba(45, 63, 204, 0.08), rgba(123, 63, 228, 0.08))', border: '1px solid var(--primary)', borderRadius: 10, padding: '14px 16px', marginTop: 10, fontSize: 13, color: 'var(--navy)' }}>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>💡 Suggested competitive pricing</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 10 }}>
              <div style={{ background: 'white', borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Hourly</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary)' }}>${suggested.hourly}/hr</div>
              </div>
              <div style={{ background: 'white', borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Daily</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary)' }}>${suggested.daily}/day</div>
              </div>
              <div style={{ background: 'white', borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Weekly</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary)' }}>${suggested.weekly}/wk</div>
              </div>
              <div style={{ background: 'white', borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Deposit</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary)' }}>${suggested.deposit}</div>
              </div>
            </div>
            <button className="btn btn-primary btn-sm btn-full" onClick={applyAllSuggested}>
              Apply all suggested prices
            </button>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, textAlign: 'center' }}>
              Based on Aussie hire industry rates. You can still edit any value below.
            </div>
            <div style={{ background: '#E8F8EE', border: '1px solid #16A34A', borderRadius: 10, padding: '12px 14px', marginTop: 12, fontSize: 12, color: '#15803D' }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>💵 Your earnings on suggested prices (after 12% fee)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, lineHeight: 1.5 }}>
                <div>Hourly: <strong>${(suggested.hourly * 0.88).toFixed(2)}</strong></div>
                <div>Daily: <strong>${(suggested.daily * 0.88).toFixed(2)}</strong></div>
                <div>Weekly: <strong>${(suggested.weekly * 0.88).toFixed(2)}</strong></div>
                <div>Deposit: <strong>${suggested.deposit}</strong> (refunded)</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ background: 'var(--primary-pale)', borderLeft: '3px solid var(--primary)', borderRadius: 10, padding: '14px 16px', marginBottom: 12, fontSize: 13, color: 'var(--navy)', lineHeight: 1.6 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>💰 Set at least one rate — pick the hire periods you'll accept</div>
        <div>You can offer hourly, daily, weekly, or any combination. Setting more than one gives hirers flexibility and tends to attract more bookings.</div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Price per hour <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
          <input className="form-control" type="number" placeholder="0.00" min="1" step="0.50" value={form.price_per_hour} onChange={e => set('price_per_hour', e.target.value)} />
          <div className="form-hint">Leave blank if you only accept full-day/week hires. Otherwise you'll receive ~88% after platform fee.</div>
        </div>
        <div className="form-group">
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Security deposit (optional)
            <span
              style={{ position: 'relative', display: 'inline-block', lineHeight: 1 }}
              onMouseEnter={() => setDepositTooltip(true)}
              onMouseLeave={() => setDepositTooltip(false)}
              onClick={() => setDepositTooltip(v => !v)}
            >
              <span style={{ cursor: 'pointer', color: 'var(--text-faint)', fontSize: 14, userSelect: 'none' }}>ⓘ</span>
              {depositTooltip && (
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: 'white', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.15)', padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)', width: 280, maxWidth: 'calc(100vw - 32px)', zIndex: 100, lineHeight: 1.5, fontWeight: 400 }}>
                  This deposit is held securely in Stripe — it's not charged to the hirer and doesn't go to the owner. It's a temporary hold, released automatically once the item is returned in good condition. If there's damage or the item isn't returned, the owner can draw on this deposit to cover it.
                </div>
              )}
            </span>
          </label>
          <input className="form-control" type="number" placeholder="0.00" min="0" value={form.deposit} onChange={e => set('deposit', e.target.value)} />
          {suggested && !form.deposit && (
            <div className="form-hint" style={{ cursor: 'pointer', color: 'var(--primary)', fontWeight: 500 }} onClick={() => set('deposit', suggested.deposit)}>
              💡 Suggested: ${suggested.deposit} — tap to apply
            </div>
          )}
          {!suggested && fallbackDeposit && !form.deposit && (
            <div className="form-hint" style={{ cursor: 'pointer', color: 'var(--primary)', fontWeight: 500 }} onClick={() => set('deposit', fallbackDeposit)}>
              💡 Suggested: ${fallbackDeposit} — tap to apply
            </div>
          )}
          {!suggested && !fallbackDeposit && (
            <div className="form-hint">Refunded when item returned safely</div>
          )}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Price per day <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(recommended)</span></label>
          <input className="form-control" type="number" placeholder="0.00" min="0" step="0.50" value={form.price_per_day} onChange={e => set('price_per_day', e.target.value)} />
          {suggested && !form.price_per_day && (
            <div className="form-hint" style={{ cursor: 'pointer', color: 'var(--primary)', fontWeight: 500 }} onClick={() => set('price_per_day', suggested.daily)}>
              💡 Suggested: ${suggested.daily}/day — tap to apply
            </div>
          )}
          {!suggested && (
            <div className="form-hint">📈 Hirers often want daily rates — set this to attract multi-day bookings</div>
          )}
          {form.price_per_hour && form.price_per_day && parseFloat(form.price_per_day) < parseFloat(form.price_per_hour) * 3 && (
            <div className="form-hint">💡 Your daily rate is close to your hourly rate. Most owners set daily at 4-6x hourly so longer hires are better value.</div>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Price per week <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(recommended)</span></label>
          <input className="form-control" type="number" placeholder="0.00" min="0" step="0.50" value={form.price_per_week} onChange={e => set('price_per_week', e.target.value)} />
          {suggested && !form.price_per_week && (
            <div className="form-hint" style={{ cursor: 'pointer', color: 'var(--primary)', fontWeight: 500 }} onClick={() => set('price_per_week', suggested.weekly)}>
              💡 Suggested: ${suggested.weekly}/wk — tap to apply
            </div>
          )}
          {!suggested && (
            <div className="form-hint">📈 Weekly hires (longer stays) — leave blank if not interested</div>
          )}
          {form.price_per_day && form.price_per_week && parseFloat(form.price_per_week) < parseFloat(form.price_per_day) * 3 && (
            <div className="form-hint">💡 Your weekly rate is close to your daily rate. Most owners set weekly at 4-6x daily so longer hires are better value.</div>
          )}
        </div>
      </div>

      {hasAnyPrice && (
        <div style={{ background: '#E8F8EE', border: '1px solid #16A34A', borderRadius: 10, padding: '12px 14px', marginTop: 4, marginBottom: 12, fontSize: 13, color: '#15803D' }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>💵 Your earnings after 12% platform fee</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, lineHeight: 1.5, fontSize: 13 }}>
            {form.price_per_hour && <div>Hourly: <strong>${(parseFloat(form.price_per_hour) * 0.88).toFixed(2)}/hr</strong></div>}
            {form.price_per_day && <div>Daily: <strong>${(parseFloat(form.price_per_day) * 0.88).toFixed(2)}/day</strong></div>}
            {form.price_per_week && <div>Weekly: <strong>${(parseFloat(form.price_per_week) * 0.88).toFixed(2)}/wk</strong></div>}
            {form.deposit && <div>Deposit: <strong>${parseFloat(form.deposit).toFixed(2)}</strong> (refunded)</div>}
          </div>
          <div style={{ fontSize: 11, marginTop: 8, opacity: 0.85 }}>
            💡 Deposits are refunded to the hirer when item is returned in good condition. No platform fee taken from deposits.
          </div>
          <div style={{ fontSize: 11, marginTop: 6, opacity: 0.85 }}>
            🎁 Launch offer: your first qualifying hire within 30 days of signing up earns a reduced platform fee.{' '}
            <a href="/how-it-works" style={{ color: '#15803D', fontWeight: 600, textDecoration: 'underline' }}>See FAQ for details →</a>
          </div>
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Minimum hire period</label>
          <select className="form-control" value={form.min_hours} onChange={e => set('min_hours', parseInt(e.target.value))}>
            {hireOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div className="form-hint">Shortest hire you'll accept</div>
        </div>
        <div className="form-group">
          <label className="form-label">Maximum hire period</label>
          <select className="form-control" value={form.max_hours} onChange={e => set('max_hours', parseInt(e.target.value))}>
            {hireOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div className="form-hint">Longest hire you'll accept</div>
        </div>
      </div>

      <hr className="divider" />
      <div className="form-group">
        <label className="form-label">Location <span style={{ color: '#DC2626' }}>*</span> <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(required)</span></label>
        <div ref={autocompleteContainerRef} className="form-control" style={{ padding: 0, height: 'auto' }}></div>
        <div className="form-hint">Start typing your suburb or address</div>
      </div>
      {form.full_address && (
        <div style={{ background: 'var(--primary-light)', color: 'var(--primary)', padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500 }}>
          📍 {form.full_address}
        </div>
      )}
      </>
    )}

    {/* ── Step 3: Confirm ── */}
    {step === 3 && (
      <>
      <div style={{ background: 'var(--gray-50)', borderRadius: 12, padding: '1rem', marginBottom: '1rem' }}>
        <div style={{ fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>Listing summary</div>
        {[
          { label: 'Item', val: form.title },
          { label: 'Category', val: CATEGORIES.find(c => c.id === parseInt(form.category_id))?.name },
          { label: 'Location', val: form.full_address || `${form.suburb}, ${form.state}` },
          { label: 'Country', val: form.country },
          form.price_per_hour && { label: 'Price/hour', val: `$${parseFloat(form.price_per_hour).toFixed(2)}/hr` },
          { label: 'Price/day', val: form.price_per_day ? `$${parseFloat(form.price_per_day).toFixed(2)}/day` : 'Not set' },
          { label: 'Price/week', val: form.price_per_week ? `$${parseFloat(form.price_per_week).toFixed(2)}/week` : 'Not set' },
          { label: 'Deposit', val: `$${parseFloat(form.deposit || 0).toFixed(2)}` },
          { label: 'Item value', val: form.item_value ? `$${parseFloat(form.item_value).toFixed(2)}` : 'Not specified' },
          { label: 'Min hire', val: MIN_HIRE_OPTIONS.find(o => o.value === form.min_hours)?.label || `${form.min_hours} hours` },
          { label: 'Max hire', val: MIN_HIRE_OPTIONS.find(o => o.value === form.max_hours)?.label || `${form.max_hours} hours` },
          { label: 'ID required', val: form.requires_id ? "🪪 Yes — driver's licence at pickup" : 'No' },
          { label: 'Operator', val: form.includes_operator ? "🛠️ Included — you'll operate the equipment" : 'Not included' },
          { label: 'Insurance', val: form.owner_insured === true ? '🔵 Covered by my own insurance during the hire period' : form.owner_insured === false ? '🟡 No insurance cover on this item' : 'Not specified' },
          { label: 'Photos', val: `${images.length} uploaded` },
          form.price_per_hour && { label: 'You earn (est.)', val: `$${(parseFloat(form.price_per_hour) * 0.88).toFixed(2)}/hr after platform fee` },
        ].filter(Boolean).map(row => (
          <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
            <span style={{ color: 'var(--text-muted)' }}>{row.label}</span>
            <span style={{ fontWeight: 600, color: 'var(--navy)' }}>{row.val}</span>
          </div>
        ))}
      </div>

      {missingMultiDayRates && (
        <div style={{ background: '#FFF8E7', border: '1px solid #F5C842', borderRadius: 10, padding: '12px 14px', marginBottom: 12, fontSize: 13, color: '#8a6d00', lineHeight: 1.6 }}>
          ⚠️ <strong>Heads up:</strong> You haven't set a daily or weekly rate. Hirers wanting your item for multiple days will pay hourly × 24, which is often too expensive — they may pick another listing.<br />
          <button type="button" onClick={() => setStep(2)} style={{ background: 'none', border: 'none', color: '#8a6d00', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontWeight: 700, marginTop: 4 }}>← Go back and add rates</button>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Insurance status</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ display: 'flex', gap: 10, cursor: 'pointer', fontSize: 13, padding: '10px 14px', borderRadius: 8, border: `1px solid ${form.owner_insured === true ? 'var(--primary)' : 'var(--border)'}`, background: form.owner_insured === true ? 'var(--primary-light)' : 'white' }}>
            <input type="radio" name="insurance" checked={form.owner_insured === true} onChange={() => set('owner_insured', true)} />
            <span>🔵 <strong>Covered by my own insurance during the hire period</strong></span>
          </label>
          <label style={{ display: 'flex', gap: 10, cursor: 'pointer', fontSize: 13, padding: '10px 14px', borderRadius: 8, border: `1px solid ${form.owner_insured === false ? 'var(--amber)' : 'var(--border)'}`, background: form.owner_insured === false ? '#FAEEDA' : 'white' }}>
            <input type="radio" name="insurance" checked={form.owner_insured === false} onChange={() => set('owner_insured', false)} />
            <span>🟡 <strong>No insurance cover on this item</strong></span>
          </label>
        </div>
      </div>

      <div style={{ border: '1px solid var(--border-strong)', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
        <label style={{ display: 'flex', gap: 10, cursor: 'pointer', fontSize: 13, lineHeight: 1.6 }}>
          <input type="checkbox" checked={form.insureAgree} onChange={e => set('insureAgree', e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>{form.owner_insured === true ? 'I confirm my own insurance covers this item during hire. I understand hirers remain responsible for arranging their own cover, and that HireIt bears no liability for any loss, damage or injury.' : 'I confirm the hirer is solely responsible for insuring this item during hire. I understand HireIt bears no liability for any loss, damage or injury.'}</span>
        </label>
      </div>
      <div style={{ border: '1px solid var(--border-strong)', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
        <label style={{ display: 'flex', gap: 10, cursor: 'pointer', fontSize: 13, lineHeight: 1.6 }}>
          <input type="checkbox" checked={form.ownershipAgree} onChange={e => set('ownershipAgree', e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>I confirm I own this item, or have the right to hire it out, and that the information in this listing is accurate.</span>
        </label>
      </div>
      <div style={{ border: '1px solid var(--border-strong)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
        <label style={{ display: 'flex', gap: 10, cursor: 'pointer', fontSize: 13, lineHeight: 1.6 }}>
          <input type="checkbox" checked={form.termsAgree} onChange={e => set('termsAgree', e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>I agree to HireIt's Terms & Conditions, Listing Policy, and understand the 12% platform commission applies to each booking.</span>
        </label>
      </div>
      </>
    )}

    {/* ── Step 4: Create account / Sign in ── */}
    {step === 4 && (
      <>
      {/* Sign-up mode */}
      {s4Mode === 'signup' && (
        <>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🎉</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>Almost there!</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>Create your free account to publish this listing.</p>
        </div>

        <div className="form-group">
          <label className="form-label">Full name</label>
          <input className="form-control" placeholder="Your full name" value={s4Name} onChange={e => setS4Name(e.target.value)} autoComplete="name" autoCapitalize="words" style={s4Errors.name ? { borderColor: 'var(--red)' } : undefined} />
          {s4Errors.name && <div className="form-error" style={{ marginTop: 6 }}>{s4Errors.name}</div>}
        </div>
        <div className="form-group">
          <label className="form-label">Email address</label>
          <input className="form-control" type="email" inputMode="email" autoCapitalize="none" spellCheck={false} placeholder="you@email.com" value={s4Email} onChange={e => setS4Email(e.target.value)} autoComplete="email" style={s4Errors.email ? { borderColor: 'var(--red)' } : undefined} />
          {s4Errors.email && (
            <div className="form-error" style={{ marginTop: 6 }}>
              {s4Errors.email}
              {s4Errors.alreadyRegistered && (
                <> <button type="button" onClick={() => { track('sign_up_switch', { to: 'login', source: 'list_item' }); setS4Mode('signin'); setS4Errors({}); setS4Error('') }} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: 'inherit' }}>Sign in</button></>
              )}
            </div>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Password</label>
          <input className="form-control" type="password" placeholder="At least 6 characters" value={s4Password} onChange={e => setS4Password(e.target.value)} autoComplete="new-password"
            onKeyDown={e => e.key === 'Enter' && handleS4SignUp()} style={s4Errors.password ? { borderColor: 'var(--red)' } : undefined} />
          {s4Errors.password && <div className="form-error" style={{ marginTop: 6 }}>{s4Errors.password}</div>}
        </div>

        <div style={{ border: '1px solid var(--border-strong)', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ maxHeight: 170, overflowY: 'auto', paddingRight: 8, marginBottom: 10 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', margin: '0 0 8px', letterSpacing: 0.3 }}>IMPORTANT</p>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 6px' }}>
              By completing and submitting this form You will create a binding agreement between You and Us incorporating the <a href="/terms" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Standard Terms and Conditions</a>.
            </p>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 4px' }}>By completing and submitting this form You warrant that:</p>
            <ul style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 6px', paddingLeft: 18 }}>
              <li>You have read the Terms and Conditions;</li>
              <li>You have not submitted more than one Application (unless We have given You permission otherwise);</li>
              <li>You are over 18 years of age; and</li>
              <li>all the information given in this Application is correct.</li>
            </ul>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 4px' }}>Your use of the Platform will be governed by the Terms and Conditions and You acknowledge that:</p>
            <ul style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 6px', paddingLeft: 18 }}>
              <li>You must not use, and must cease to use, the Platform if You do not agree or cease to agree to the Terms and Conditions; and</li>
              <li>You have the opportunity to obtain independent legal advice about the Terms and Conditions.</li>
            </ul>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>
              Words and phrases used in this form have the meanings set out in the Terms and Conditions and will be interpreted in accordance with the rules of interpretation set out therein.
            </p>
          </div>
          <label style={{ display: 'flex', gap: 10, cursor: 'pointer', fontSize: 13, lineHeight: 1.6, borderTop: '1px solid var(--border-strong)', paddingTop: 10 }}>
            <input type="checkbox" checked={s4Terms} onChange={e => setS4Terms(e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
            <span>I have read and agree to the <a href="/terms" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Terms and Conditions</a>.</span>
          </label>
        </div>

        {s4Errors.terms && <div className="form-error" style={{ marginTop: -4, marginBottom: 12 }}>{s4Errors.terms}</div>}
        {s4Errors.general && <div className="alert alert-error">{s4Errors.general}</div>}

        <button className="btn btn-gradient btn-full" onClick={handleS4SignUp} disabled={s4Loading}>
          {s4Loading ? 'Creating account...' : 'Create free account & publish'}
        </button>

        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)', marginTop: 16, marginBottom: 0 }}>
          Already have an account?{' '}
          <button onClick={() => { track('sign_up_switch', { to: 'login', source: 'list_item' }); setS4Mode('signin'); setS4Error(''); setS4Errors({}) }} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 500, cursor: 'pointer', fontSize: 13 }}>
            Sign in
          </button>
        </p>
        </>
      )}

      {/* Pending verification mode — shown after sign-up */}
      {s4Mode === 'pending' && (
        <>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>✉️</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>Check your inbox</h2>
          <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 0 }}>
            We've sent a confirmation link to <strong>{s4Email}</strong>. Click it to confirm your account, then come back to this page and sign in below to publish your listing.
          </p>
        </div>

        <div style={{ background: '#E8F8EE', border: '1px solid #16A34A', borderRadius: 10, padding: '12px 14px', marginBottom: 20, fontSize: 13, color: '#15803D', textAlign: 'center' }}>
          Keep this page open — your listing and photos are ready to publish once you sign in.
        </div>

        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)', marginBottom: 12 }}>Already confirmed? Sign in to publish:</div>

        <div className="form-group">
          <label className="form-label">Email address</label>
          <input className="form-control" type="email" value={s4Email} onChange={e => setS4Email(e.target.value)} autoComplete="email" />
        </div>
        <div className="form-group">
          <label className="form-label">Password</label>
          <input className="form-control" type="password" placeholder="••••••••" value={s4Password} onChange={e => setS4Password(e.target.value)} autoComplete="current-password"
            onKeyDown={e => e.key === 'Enter' && handleS4SignIn()} />
        </div>

        {s4Error && <div className="alert alert-error">{s4Error}</div>}

        <button className="btn btn-gradient btn-full" onClick={handleS4SignIn} disabled={s4Loading}>
          {s4Loading ? 'Signing in...' : 'Sign in & publish'}
        </button>
        </>
      )}

      {/* Sign-in mode — for existing accounts */}
      {s4Mode === 'signin' && (
        <>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>Sign in to publish</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>Your listing draft is saved and ready to go.</p>
        </div>

        <div className="form-group">
          <label className="form-label">Email address</label>
          <input className="form-control" type="email" placeholder="you@email.com" value={s4Email} onChange={e => setS4Email(e.target.value)} autoComplete="email" />
        </div>
        <div className="form-group">
          <label className="form-label">Password</label>
          <input className="form-control" type="password" placeholder="••••••••" value={s4Password} onChange={e => setS4Password(e.target.value)} autoComplete="current-password"
            onKeyDown={e => e.key === 'Enter' && handleS4SignIn()} />
        </div>

        {s4Error && <div className="alert alert-error">{s4Error}</div>}

        <button className="btn btn-gradient btn-full" onClick={handleS4SignIn} disabled={s4Loading}>
          {s4Loading ? 'Signing in...' : 'Sign in & publish'}
        </button>

        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)', marginTop: 16, marginBottom: 0 }}>
          No account yet?{' '}
          <button onClick={() => { track('sign_up_switch', { to: 'signup', source: 'list_item' }); setS4Mode('signup'); setS4Error(''); setS4Errors({}) }} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 500, cursor: 'pointer', fontSize: 13 }}>
            Create one
          </button>
        </p>
        </>
      )}
      </>
    )}

    {step >= 2 && <ContactWarning text={`${form.title}\n${form.description}`} />}
    {error && <div className="alert alert-error">{error}</div>}

    {/* Navigation buttons */}
    <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', marginTop: step === 4 ? 0 : undefined }}>
      {step > 1 && step < 4 && (
        <button className="btn btn-ghost" onClick={() => { setStep(s => s - 1); setError('') }}>Back</button>
      )}
      {step === 4 && s4Mode !== 'pending' && (
        <button className="btn btn-ghost" onClick={() => { setStep(3); setError('') }}>Back</button>
      )}
      {step < 3 && (
        <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={handleNext}>Next →</button>
      )}
      {step === 3 && (
        <button
          className="btn btn-gradient"
          style={{ marginLeft: 'auto' }}
          onClick={user ? () => doPublish(pendingFilesRef.current) : handleStep3Continue}
          disabled={submitting || uploading}
        >
          {submitting ? 'Publishing...' : uploading ? 'Uploading photos...' : user ? 'Publish listing' : 'Continue →'}
        </button>
      )}
    </div>

  </div>
</div>
</div>
)
}
